import { useEffect, useRef, useState } from "react";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Box,
  Card,
  CardContent,
  Chip,
  ClickAwayListener,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import FlexBox from "../../../../components/layouts/flexbox";
import type { AmbassadorInitiative } from "./types";
import { formatTargetCompletion, getInitiativeTypeDisplayLabel } from "./utils";

const SUMMARY_LINE_CLAMP = 3;
const FALLBACK_SUMMARY = "No description.";

function InitiativeCard({ initiative }: { initiative: AmbassadorInitiative }) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const summaryRef = useRef<HTMLParagraphElement | null>(null);
  const summaryText = initiative.summary?.trim() || FALLBACK_SUMMARY;
  const showToolName =
    initiative.isToolNameRequired || initiative.type === "replace tool";

  useEffect(() => {
    const element = summaryRef.current;
    if (!element) return;
    setShowReadMore(element.scrollHeight > element.clientHeight);
  }, [summaryText]);

  const handleReadMoreClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setTooltipOpen((prev) => !prev);
  };

  const tooltipContent = (
    <Paper
      elevation={3}
      onClick={(event) => event.stopPropagation()}
      sx={{
        p: 2,
        maxWidth: 520,
        maxHeight: 400,
        overflow: "auto",
      }}
    >
      <Typography fontSize={13} color="#111827" whiteSpace="pre-wrap">
        {summaryText}
      </Typography>
    </Paper>
  );

  return (
    <Card
      sx={{
        height: "100%",
        border: "1px solid #E5E7EB",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack gap={1.5}>
          <FlexBox alignItems="center" justifyContent="space-between" gap={1}>
            <FlexBox alignItems="center" gap={0.75}>
              {initiative.isPublic ? (
                <VisibilityOutlinedIcon
                  htmlColor="#2563EB"
                  sx={{ fontSize: 18 }}
                />
              ) : (
                <VisibilityOffOutlinedIcon
                  htmlColor="#D97706"
                  sx={{ fontSize: 18 }}
                />
              )}
              <Typography
                fontSize={12}
                fontWeight={600}
                color={initiative.isPublic ? "#2563EB" : "#D97706"}
              >
                {initiative.isPublic ? "Public" : "Private"}
              </Typography>
            </FlexBox>
            {initiative.isActive && (
              <Chip
                label="Active"
                size="small"
                sx={{
                  bgcolor: "#DCFCE7",
                  color: "#166534",
                  fontWeight: 600,
                  fontSize: 11,
                }}
              />
            )}
          </FlexBox>

          <Divider />

          <Typography fontSize={13} fontWeight={600} color="#166534">
            {getInitiativeTypeDisplayLabel(initiative.type)}
          </Typography>

          <Typography
            fontSize={16}
            fontWeight={600}
            color="#111827"
            overflow="hidden"
            textOverflow="ellipsis"
            display="-webkit-box"
            sx={{
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
            title={initiative.title}
          >
            {initiative.title}
          </Typography>

          {showToolName && initiative.toolName && (
            <Box>
              <Typography fontSize={11} fontWeight={600} color="#6B7280" mb={0.25}>
                Tool
              </Typography>
              <Typography fontSize={13} color="#374151">
                {initiative.toolName}
              </Typography>
            </Box>
          )}

          <FlexBox alignItems="center" gap={0.75}>
            <CalendarMonthOutlinedIcon
              htmlColor="#6B7280"
              sx={{ fontSize: 16 }}
            />
            <Typography fontSize={12} fontWeight={600} color="#6B7280">
              Target Completion:
            </Typography>
            <Typography fontSize={13} color="#374151">
              {formatTargetCompletion(initiative.plannedDeliveryDate)}
            </Typography>
          </FlexBox>

          <Stack flex={1} minHeight={0} gap={0.25}>
            <Typography
              ref={summaryRef}
              fontSize={13}
              color="#4B5563"
              lineHeight={1.5}
              overflow="hidden"
              display="-webkit-box"
              sx={{
                WebkitLineClamp: SUMMARY_LINE_CLAMP,
                WebkitBoxOrient: "vertical",
              }}
            >
              {summaryText}
            </Typography>
            {showReadMore && (
              <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
                <Box component="span" display="inline-block">
                  <Tooltip
                    open={tooltipOpen}
                    onClose={() => setTooltipOpen(false)}
                    title={tooltipContent}
                    placement="right"
                    arrow
                    disableHoverListener
                    disableFocusListener
                    PopperProps={{
                      onClick: (event: React.MouseEvent) =>
                        event.stopPropagation(),
                    }}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "transparent",
                          p: 0,
                          maxWidth: "none",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "background.paper",
                          "&::before": {
                            border: "1px solid",
                            borderColor: "divider",
                          },
                        },
                      },
                    }}
                  >
                    <Typography
                      component="button"
                      type="button"
                      fontSize={13}
                      color="primary"
                      fontWeight={500}
                      align="left"
                      onClick={handleReadMoreClick}
                      aria-label="Read full summary"
                      border={0}
                      p={0}
                      sx={{
                        cursor: "pointer",
                        bgcolor: "transparent",
                        "&:hover": { textDecoration: "underline", border: "none" },
                      }}
                    >
                      Read more
                    </Typography>
                  </Tooltip>
                </Box>
              </ClickAwayListener>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export { InitiativeCard };
